import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, IconButton,
  useTheme, useMediaQuery
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import CancelIcon from '@mui/icons-material/Cancel';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || '';

const ESTADO_COLOR = {
  pendiente: 'warning', confirmado: 'success', cancelado: 'error',
  atendido: 'info', en_recepcion: 'secondary', demorado: 'default',
};

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_SEMANA_FC = ['Domingo','Lunes','Martes','Miercoles','Jueves','Viernes','Sabado'];

const FORM_VACIO = { medico_id: '', motivo: '', fecha: '', hora_inicio: '', mensaje_paciente: '' };

// ── Mini Calendario ──────────────────────────────────────────────────────────
function MiniCalendario({ medicoId, horarios, onSelectFecha, fechaSeleccionada }) {
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth());
  const [ocupacion, setOcupacion] = useState({});
  const [cargando, setCargando] = useState(false);

  const mesStr = `${anio}-${String(mes + 1).padStart(2, '0')}`;

  useEffect(() => {
    if (!medicoId) return;
    setCargando(true);
    axios.get(`${API}/api/medicos/${medicoId}/ocupacion-mes?mes=${mesStr}`)
      .then(r => setOcupacion(r.data))
      .catch(() => setOcupacion({}))
      .finally(() => setCargando(false));
  }, [medicoId, mesStr]);

  function calcularColorDia(dia) {
    const fecha = new Date(anio, mes, dia);
    const hoyMedNoche = new Date(); hoyMedNoche.setHours(0, 0, 0, 0);
    if (fecha < hoyMedNoche) return 'pasado';
    const fechaStr = `${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
    if (ocupacion[fechaStr] === 'ausente') return 'ausente';
    const horario = (horarios || []).find(h => h.dia === DIAS_SEMANA_FC[fecha.getDay()]);
    if (!horario) return 'sin_horario';
    const [hI, mI] = horario.desde.split(':').map(Number);
    const [hF, mF] = horario.hasta.split(':').map(Number);
    const dur = parseInt(horario.duracion) || 30;
    const totalSlots = Math.floor(((hF * 60 + mF) - (hI * 60 + mI)) / dur);
    return (ocupacion[fechaStr] || 0) >= totalSlots ? 'lleno' : 'libre';
  }

  function prevMes() {
    if (mes === 0) { setAnio(a => a - 1); setMes(11); } else setMes(m => m - 1);
  }
  function nextMes() {
    if (mes === 11) { setAnio(a => a + 1); setMes(0); } else setMes(m => m + 1);
  }

  const diasEnMes = new Date(anio, mes + 1, 0).getDate();
  let inicioGrid = new Date(anio, mes, 1).getDay() - 1;
  if (inicioGrid < 0) inicioGrid = 6;

  const celdas = [];
  for (let i = 0; i < inicioGrid; i++) celdas.push(null);
  for (let d = 1; d <= diasEnMes; d++) celdas.push(d);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <IconButton onClick={prevMes} size="small" sx={{ color: 'var(--c-acento)' }}><ChevronLeftIcon /></IconButton>
        <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: 14 }}>
          {MESES[mes]} {anio}
          {cargando && <CircularProgress size={12} sx={{ color: 'var(--c-acento)', ml: 1 }} />}
        </Typography>
        <IconButton onClick={nextMes} size="small" sx={{ color: 'var(--c-acento)' }}><ChevronRightIcon /></IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', mb: '4px' }}>
        {['Lu','Ma','Mi','Ju','Vi','Sá','Do'].map(d => (
          <Typography key={d} sx={{ color: 'var(--c-acento)', fontSize: 11, textAlign: 'center', fontWeight: 600 }}>{d}</Typography>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
        {celdas.map((dia, i) => {
          if (!dia) return <Box key={`e${i}`} />;
          const color = calcularColorDia(dia);
          const fechaStr = `${anio}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
          const seleccionado = fechaSeleccionada === fechaStr;
          const clickable = color === 'libre';

          return (
            <Box key={dia} onClick={() => clickable && onSelectFecha(fechaStr)}
              sx={{
                height: 34, borderRadius: 1, display: 'flex', alignItems: 'center',
                justifyContent: 'center', cursor: clickable ? 'pointer' : 'default',
                bgcolor: seleccionado ? '#1565c0'
                  : color === 'libre'    ? 'rgba(102,187,106,0.18)'
                  : color === 'lleno'    ? 'rgba(239,83,80,0.12)'
                  : color === 'ausente'  ? 'rgba(100,100,100,0.18)'
                  : 'transparent',
                border: seleccionado ? '1px solid #42a5f5'
                  : color === 'libre'    ? '1px solid rgba(102,187,106,0.35)'
                  : color === 'lleno'    ? '1px solid rgba(239,83,80,0.25)'
                  : color === 'ausente'  ? '1px solid rgba(100,100,100,0.3)'
                  : 'none',
                '&:hover': clickable ? { bgcolor: 'rgba(102,187,106,0.32)', border: '1px solid #66bb6a' } : {},
              }}>
              <Typography sx={{
                fontSize: 13, userSelect: 'none',
                color: seleccionado ? '#fff'
                  : color === 'libre'   ? '#66bb6a'
                  : color === 'lleno'   ? '#ef5350'
                  : color === 'ausente' ? '#666'
                  : '#3a3a4a',
                fontWeight: seleccionado ? 700 : 400,
              }}>
                {dia}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
        {[
          { c: '#66bb6a', l: 'Disponible' },
          { c: '#ef5350', l: 'Completo' },
          { c: '#666', l: 'Ausente' },
        ].map(({ c, l }) => (
          <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c }} />
            <Typography sx={{ fontSize: 11, color: '#b0b0b0' }}>{l}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── SlotPicker ───────────────────────────────────────────────────────────────
function SlotPicker({ slots, cargando, seleccionado, duracion, onSelect }) {
  if (cargando) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
      <CircularProgress size={28} sx={{ color: 'var(--c-acento)' }} />
    </Box>
  );
  if (!slots.length) return null;
  return (
    <Box>
      <Typography sx={{ color: 'var(--c-acento)', fontSize: 13, mb: 1 }}>
        Horarios disponibles ({duracion} min c/u):
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {slots.map(s => (
          <Button key={s} size="small"
            variant={seleccionado === s ? 'contained' : 'outlined'}
            onClick={() => onSelect(s)}
            sx={{
              minWidth: 72, borderColor: 'var(--c-primario)',
              color: seleccionado === s ? '#fff' : 'var(--c-acento)',
              bgcolor: seleccionado === s ? 'var(--c-primario)' : 'transparent',
              '&:hover': { bgcolor: 'var(--c-primario)', color: '#fff' },
            }}>
            {s}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

// ── TarjetaTurno ─────────────────────────────────────────────────────────────
function TarjetaTurno({ turno: t, formatFecha, pasado, onCancelar, onReprogramar }) {
  const cancelable = !pasado && !['cancelado', 'atendido'].includes(t.estado);
  return (
    <Box sx={{ bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 2, mb: 1.5, border: '1px solid var(--c-primario)', opacity: pasado ? 0.65 : 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontWeight: 600, color: '#e0e0e0', fontSize: 15 }}>
            {t.medico_nombre || 'Médico'} · {t.especialidad_nombre || t.especialidad || ''}
          </Typography>
          <Typography sx={{ color: 'var(--c-acento)', fontSize: 13, mt: 0.3 }}>
            {formatFecha(t.fecha)} a las {String(t.hora_inicio).substring(0, 5)}hs
            {t.motivo ? ` · ${t.motivo}` : ''}
          </Typography>
        </Box>
        <Chip label={t.estado || 'pendiente'} color={ESTADO_COLOR[t.estado] || 'default'} size="small" />
      </Box>
      {t.mensaje_paciente && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(144,202,249,0.08)', borderRadius: 1, borderLeft: '3px solid var(--c-acento)' }}>
          <Typography sx={{ fontSize: 12, color: 'var(--c-acento)', fontWeight: 600 }}>Tu mensaje:</Typography>
          <Typography sx={{ fontSize: 12, color: '#e0e0e0' }}>{t.mensaje_paciente}</Typography>
        </Box>
      )}
      {t.nota_medico && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'rgba(102,187,106,0.08)', borderRadius: 1, borderLeft: '3px solid #66bb6a' }}>
          <Typography sx={{ fontSize: 12, color: '#66bb6a', fontWeight: 600 }}>Nota del equipo:</Typography>
          <Typography sx={{ fontSize: 12, color: '#e0e0e0' }}>{t.nota_medico}</Typography>
        </Box>
      )}
      {cancelable && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button size="small" startIcon={<EventRepeatIcon />} onClick={onReprogramar}
            sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', border: '1px solid', fontSize: 12 }}>
            Reprogramar
          </Button>
          <Button size="small" startIcon={<CancelIcon />} onClick={onCancelar}
            sx={{ color: '#ef9a9a', borderColor: '#4a1a1a', border: '1px solid', fontSize: 12 }}>
            Cancelar
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ── Panel principal ──────────────────────────────────────────────────────────
export default function PanelPaciente({ usuario }) {
  const navigate = useNavigate();
  const [turnos, setTurnos] = useState([]);
  const [medicos, setMedicos] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [datosPaciente, setDatosPaciente] = useState(null);
  const [cargando, setCargando] = useState(true);

  // Dialog pedir turno
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paso, setPaso] = useState(1);
  const [modoBusqueda, setModoBusqueda] = useState('especialidad'); // 'especialidad' | 'medico'
  const [especialidadSel, setEspecialidadSel] = useState('');
  const [form, setForm] = useState(FORM_VACIO);
  const [horariosMedico, setHorariosMedico] = useState([]);
  const [slots, setSlots] = useState([]);
  const [duracionSlot, setDuracionSlot] = useState(30);
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState('');

  // Dialog reprogramar
  const [reprogramarOpen, setReprogramarOpen] = useState(false);
  const [turnoReprogramando, setTurnoReprogramando] = useState(null);
  const [reprogramarFecha, setReprogramarFecha] = useState('');
  const [reprogramarSlots, setReprogramarSlots] = useState([]);
  const [reprogramarHora, setReprogramarHora] = useState('');
  const [reprogramarDuracion, setReprogramarDuracion] = useState(30);
  const [cargandoRepSlots, setCargandoRepSlots] = useState(false);
  const [guardandoRep, setGuardandoRep] = useState(false);
  const [errorRep, setErrorRep] = useState('');

  useEffect(() => { cargarDatos(); }, []);

  async function cargarDatos() {
    setCargando(true);
    try {
      const [medicosR, espR, turnosR, datosR] = await Promise.all([
        axios.get(`${API}/api/medicos`),
        axios.get(`${API}/api/especialidades`),
        axios.get(`${API}/api/pacientes/me/turnos`),
        axios.get(`${API}/api/pacientes/me`),
      ]);
      setMedicos(medicosR.data || []);
      setEspecialidades(espR.data || []);
      setTurnos(turnosR.data.data || []);
      setDatosPaciente(datosR.data);
    } catch (err) { console.error('Error cargando datos:', err); }
    setCargando(false);
  }

  // Médicos filtrados según modo y especialidad seleccionada
  const medicosFiltrados = modoBusqueda === 'especialidad' && especialidadSel
    ? medicos.filter(m => Number(m.especialidad_id) === Number(especialidadSel))
    : medicos;

  // ── Pedir turno ─────────────────────────────────────────────────────────────
  function abrirDialog() {
    setForm(FORM_VACIO);
    setSlots([]);
    setHorariosMedico([]);
    setEspecialidadSel('');
    setErrorForm('');
    setPaso(1);
    setModoBusqueda('especialidad');
    setDialogOpen(true);
  }

  function cerrarDialog() {
    setDialogOpen(false);
    setPaso(1);
    setSlots([]);
    setErrorForm('');
  }

  async function seleccionarMedico(medicoId) {
    setForm(f => ({ ...f, medico_id: medicoId, fecha: '', hora_inicio: '' }));
    // Cargar horarios del médico para el mini calendario
    try {
      const r = await axios.get(`${API}/api/medicos/${medicoId}/disponibilidad`);
      setHorariosMedico(r.data || []);
    } catch { setHorariosMedico([]); }
  }

  async function cargarSlots(medicoId, fecha) {
    if (!medicoId || !fecha) return;
    setCargandoSlots(true);
    setSlots([]);
    setErrorForm('');
    try {
      const r = await axios.get(`${API}/api/medicos/${medicoId}/slots?fecha=${fecha}`);
      if (r.data.ausente) {
        setErrorForm(`El profesional no está disponible ese día${r.data.motivo ? `: ${r.data.motivo}` : ''}.`);
        setSlots([]);
      } else {
        setSlots(r.data.slots || []);
        setDuracionSlot(r.data.duracion || 30);
      }
    } catch { setSlots([]); }
    setCargandoSlots(false);
  }

  function handleSelectFecha(fecha) {
    setForm(f => ({ ...f, fecha, hora_inicio: '' }));
    cargarSlots(form.medico_id, fecha);
  }

  async function solicitarTurno() {
    if (!form.hora_inicio) { setErrorForm('Elegí un horario disponible.'); return; }
    setGuardando(true);
    setErrorForm('');
    try {
      await axios.post(`${API}/api/turnos`, {
        medico_id: form.medico_id,
        paciente_id: datosPaciente.id,
        paciente_dni: datosPaciente.dni || '',
        paciente_nombre: `${datosPaciente.nombre} ${datosPaciente.apellido}`.trim(),
        paciente_email: datosPaciente.email,
        paciente_telefono: datosPaciente.telefono,
        motivo: form.motivo,
        fecha: form.fecha,
        hora_inicio: form.hora_inicio,
        duracion: duracionSlot,
        estado: 'pendiente',
        mensaje_paciente: form.mensaje_paciente || null,
      });
      cerrarDialog();
      cargarDatos();
    } catch (err) {
      setErrorForm(err.response?.data?.error || 'Error al solicitar el turno.');
    } finally { setGuardando(false); }
  }

  // ── Cancelar / Reprogramar ──────────────────────────────────────────────────
  async function cancelarTurno(id) {
    if (!window.confirm('¿Cancelar este turno?')) return;
    try {
      await axios.put(`${API}/api/pacientes/me/turnos/${id}/cancelar`);
      cargarDatos();
    } catch (err) { alert(err.response?.data?.error || 'No se pudo cancelar el turno.'); }
  }

  function abrirReprogramar(turno) {
    setTurnoReprogramando(turno);
    setReprogramarFecha('');
    setReprogramarHora('');
    setReprogramarSlots([]);
    setErrorRep('');
    setReprogramarOpen(true);
  }

  async function cargarSlotsRep(fecha) {
    setReprogramarFecha(fecha);
    setReprogramarHora('');
    setReprogramarSlots([]);
    if (!fecha || !turnoReprogramando) return;
    setCargandoRepSlots(true);
    try {
      const r = await axios.get(`${API}/api/medicos/${turnoReprogramando.medico_id}/slots?fecha=${fecha}`);
      setReprogramarSlots(r.data.slots || []);
      setReprogramarDuracion(r.data.duracion || 30);
    } catch { setReprogramarSlots([]); }
    setCargandoRepSlots(false);
  }

  async function confirmarReprogramar() {
    if (!reprogramarHora) { setErrorRep('Elegí un horario.'); return; }
    setGuardandoRep(true);
    setErrorRep('');
    try {
      await axios.put(`${API}/api/pacientes/me/turnos/${turnoReprogramando.id}/reprogramar`, {
        fecha: reprogramarFecha, hora_inicio: reprogramarHora,
      });
      setReprogramarOpen(false);
      cargarDatos();
    } catch (err) {
      setErrorRep(err.response?.data?.error || 'No se pudo reprogramar.');
    } finally { setGuardandoRep(false); }
  }

  function formatFecha(fecha) {
    if (!fecha) return '-';
    return String(fecha).split('T')[0].split('-').reverse().join('/');
  }

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const hoy = new Date().toISOString().split('T')[0];
  const fieldSx = {
    '& .MuiOutlinedInput-root fieldset': { borderColor: 'var(--c-primario)' },
    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'var(--c-primario)' },
  };

  const turnosProximos = turnos.filter(t => String(t.fecha).split('T')[0] >= hoy);
  const turnosPasados  = turnos.filter(t => String(t.fecha).split('T')[0] < hoy);
  const medicoSelObj   = medicos.find(m => m.id === form.medico_id);

  if (cargando) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: 'var(--c-acento)' }} />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      {/* Bienvenida */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)' }}>
            Hola, {datosPaciente?.nombre} {datosPaciente?.apellido}
          </Typography>
          <Typography sx={{ color: '#b0b0b0', fontSize: 14, mt: 0.5 }}>
            {datosPaciente?.obra_social
              ? `${datosPaciente.obra_social}${datosPaciente.numero_afiliado ? ` · N° ${datosPaciente.numero_afiliado}` : ''}`
              : 'Sin obra social registrada'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" startIcon={<PersonIcon />} onClick={() => navigate('/mis-datos')}
            sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', border: '1px solid' }}>
            Mis datos
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirDialog}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
            Pedir turno
          </Button>
        </Box>
      </Box>

      {/* Próximos */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ color: 'var(--c-acento)', fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon fontSize="small" /> Próximos turnos
        </Typography>
        {turnosProximos.length === 0 ? (
          <Box sx={{ bgcolor: 'var(--c-superficie)', borderRadius: 2, p: 3, border: '1px solid var(--c-primario)', textAlign: 'center' }}>
            <Typography sx={{ color: '#b0b0b0', fontSize: 14 }}>No tenés turnos próximos.</Typography>
          </Box>
        ) : turnosProximos.map(t => (
          <TarjetaTurno key={t.id} turno={t} formatFecha={formatFecha}
            onCancelar={() => cancelarTurno(t.id)}
            onReprogramar={() => abrirReprogramar(t)} />
        ))}
      </Box>

      {/* Historial */}
      {turnosPasados.length > 0 && (
        <Box>
          <Typography sx={{ color: '#b0b0b0', fontWeight: 600, mb: 1.5, fontSize: 14 }}>Historial</Typography>
          {turnosPasados.slice(0, 5).map(t => (
            <TarjetaTurno key={t.id} turno={t} formatFecha={formatFecha} pasado />
          ))}
        </Box>
      )}

      {/* ── Dialog pedir turno ─────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={cerrarDialog} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {paso === 2 && (
              <IconButton size="small"
                onClick={() => { setPaso(1); setSlots([]); setErrorForm(''); }}
                sx={{ color: 'var(--c-acento)', p: 0.5 }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            {paso === 1 ? 'Pedir turno' : `Turno con ${medicoSelObj?.nombre || ''}`}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
            {[1, 2].map(n => (
              <Box key={n} sx={{ height: 3, flex: 1, borderRadius: 2, bgcolor: paso >= n ? 'var(--c-acento)' : 'var(--c-primario)' }} />
            ))}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

          {/* ── Paso 1: Elegir médico ── */}
          {paso === 1 && (
            <>
              {/* Toggle modo búsqueda */}
              <Box sx={{ display: 'flex', gap: 0, border: '1px solid var(--c-primario)', borderRadius: 1, overflow: 'hidden' }}>
                {[{ v: 'especialidad', l: 'Por especialidad' }, { v: 'medico', l: 'Por médico' }].map(({ v, l }) => (
                  <Box key={v} onClick={() => { setModoBusqueda(v); setForm(f => ({ ...f, medico_id: '' })); setEspecialidadSel(''); }}
                    sx={{
                      flex: 1, py: 1, textAlign: 'center', cursor: 'pointer', fontSize: 13,
                      bgcolor: modoBusqueda === v ? 'var(--c-primario)' : 'transparent',
                      color: modoBusqueda === v ? 'var(--c-acento)' : '#b0b0b0',
                      '&:hover': { bgcolor: 'var(--c-primario)' },
                    }}>
                    {l}
                  </Box>
                ))}
              </Box>

              {/* Selector especialidad */}
              {modoBusqueda === 'especialidad' && (
                <FormControl>
                  <InputLabel sx={{ color: 'var(--c-acento)' }}>Especialidad *</InputLabel>
                  <Select value={especialidadSel} label="Especialidad *"
                    onChange={e => { setEspecialidadSel(e.target.value); setForm(f => ({ ...f, medico_id: '' })); }}
                    sx={{ color: '#e0e0e0', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}>
                    {especialidades.length === 0
                      ? <MenuItem disabled>No hay especialidades configuradas</MenuItem>
                      : especialidades.map(e => <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>)
                    }
                  </Select>
                </FormControl>
              )}

              {/* Lista de médicos */}
              {(modoBusqueda === 'medico' || especialidadSel) && (
                <Box>
                  <Typography sx={{ color: 'var(--c-acento)', fontSize: 13, mb: 1 }}>
                    {modoBusqueda === 'especialidad' ? 'Médicos disponibles:' : 'Seleccioná un médico:'}
                  </Typography>
                  {medicosFiltrados.length === 0 ? (
                    <Typography sx={{ color: '#b0b0b0', fontSize: 13 }}>
                      No hay médicos para esta especialidad.
                    </Typography>
                  ) : medicosFiltrados.map(m => (
                    <Box key={m.id} onClick={() => seleccionarMedico(m.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1,
                        borderRadius: 1, cursor: 'pointer', border: '1px solid',
                        borderColor: form.medico_id === m.id ? '#42a5f5' : 'var(--c-primario)',
                        bgcolor: form.medico_id === m.id ? 'rgba(21,101,192,0.25)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(15,52,96,0.6)', borderColor: '#42a5f5' },
                      }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: m.color || '#1565c0', flexShrink: 0 }} />
                      <Box>
                        <Typography sx={{ fontSize: 14, color: '#e0e0e0', fontWeight: 500 }}>{m.nombre}</Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--c-acento)' }}>
                          {m.especialidad_nombre || m.especialidad || ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Motivo + mensaje */}
              {form.medico_id && (
                <>
                  <TextField label="Motivo de consulta (opcional)" value={form.motivo} multiline rows={2}
                    onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}
                    InputLabelProps={{ style: { color: 'var(--c-acento)' }, shrink: true }}
                    inputProps={{ style: { color: '#e0e0e0' } }} sx={fieldSx} />
                  <TextField
                    label="Mensaje para recepción / médico (opcional)"
                    value={form.mensaje_paciente} multiline rows={3}
                    onChange={e => setForm(f => ({ ...f, mensaje_paciente: e.target.value }))}
                    placeholder="Ej: Tengo alergia a la penicilina, vengo por control de rutina..."
                    helperText="Acá podés escribir alguna consulta o dato importante que no te quieras olvidar mencionar"
                    InputLabelProps={{ style: { color: 'var(--c-acento)' }, shrink: true }}
                    inputProps={{ style: { color: '#e0e0e0' } }}
                    FormHelperTextProps={{ style: { color: '#7a8fa6' } }}
                    sx={fieldSx} />
                </>
              )}
            </>
          )}

          {/* ── Paso 2: Elegir fecha y hora ── */}
          {paso === 2 && (
            <>
              <MiniCalendario
                medicoId={form.medico_id}
                horarios={horariosMedico}
                onSelectFecha={handleSelectFecha}
                fechaSeleccionada={form.fecha}
              />
              {form.fecha && (
                <SlotPicker slots={slots} cargando={cargandoSlots}
                  seleccionado={form.hora_inicio} duracion={duracionSlot}
                  onSelect={s => setForm(f => ({ ...f, hora_inicio: s }))} />
              )}
              {form.fecha && slots.length === 0 && !cargandoSlots && (
                <Typography sx={{ color: '#b0b0b0', fontSize: 13 }}>
                  No hay horarios disponibles para ese día.
                </Typography>
              )}
            </>
          )}

          {errorForm && <Typography sx={{ color: '#f44336', fontSize: 13 }}>{errorForm}</Typography>}
        </DialogContent>

        <DialogActions>
          <Button onClick={cerrarDialog} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          {paso === 1 ? (
            <Button
              onClick={() => { if (!form.medico_id) { setErrorForm('Seleccioná un médico.'); return; } setErrorForm(''); setPaso(2); }}
              variant="contained" sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
              Siguiente
            </Button>
          ) : (
            <Button onClick={solicitarTurno} variant="contained"
              disabled={guardando || !form.hora_inicio}
              sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
              {guardando ? 'Enviando...' : 'Confirmar turno'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Dialog reprogramar ─────────────────────────────────────────────── */}
      <Dialog open={reprogramarOpen} onClose={() => setReprogramarOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', color: '#e0e0e0' } }}>
        <DialogTitle>
          Reprogramar turno
          {turnoReprogramando && (
            <Typography sx={{ fontSize: 13, color: '#b0b0b0', mt: 0.5 }}>
              {turnoReprogramando.medico_nombre} · actual: {formatFecha(turnoReprogramando.fecha)} {String(turnoReprogramando.hora_inicio).substring(0, 5)}hs
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Nueva fecha *" type="date" value={reprogramarFecha}
            onChange={e => cargarSlotsRep(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: 'var(--c-acento)', backgroundColor: 'var(--c-superficie)', paddingLeft: 4, paddingRight: 4 } }}
            inputProps={{ style: { color: '#e0e0e0', colorScheme: 'dark' }, min: hoy }} sx={fieldSx} />
          <SlotPicker slots={reprogramarSlots} cargando={cargandoRepSlots}
            seleccionado={reprogramarHora} duracion={reprogramarDuracion}
            onSelect={setReprogramarHora} />
          {errorRep && <Typography sx={{ color: '#f44336', fontSize: 13 }}>{errorRep}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReprogramarOpen(false)} sx={{ color: 'var(--c-acento)' }}>Cancelar</Button>
          <Button onClick={confirmarReprogramar} variant="contained"
            disabled={guardandoRep || !reprogramarHora}
            sx={{ bgcolor: 'var(--c-primario)', '&:hover': { bgcolor: 'var(--c-primario)' } }}>
            {guardandoRep ? 'Guardando...' : 'Confirmar cambio'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
